import { Warranty } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  CustomTextField,
  WarrantyBrandSelect,
  WarrantyDateField,
  WarrantyFileUpload,
  WarrantyProductSelect,
  WarrantySerialNumber,
  productFields,
  useWarrantyClaimContext,
} from '@components';
import { useAuth } from '@hooks';
import { Grid } from '@mui/material';
import { t } from '@translations';
import dayjs from 'dayjs';
import { useMemo } from 'react';

export const WarrantyProductFields = () => {
  const { isBeremaUser } = useAuth();
  const { form, isSerialNoValid, isReadOnly } = useWarrantyClaimContext();
  const selectedBrand = form.watch('brand');

  const validateDate = (value: string, dateName: keyof Warranty) => {
    const selectedDate = dayjs(value);
    const today = dayjs();

    if (selectedDate.isAfter(today)) {
      return t(TextCodes.Warranty.Block.FutureDateError);
    }

    switch (dateName) {
      case 'saleDate':
        break;
      case 'damageDate':
        if (form.watch('saleDate') && selectedDate.isBefore(dayjs(form.watch('saleDate')))) {
          return t(TextCodes.Warranty.Block.DamageDateError);
        }
        break;
      case 'repairDate':
        const latestValidDate =
          form.watch('saleDate') && form.watch('damageDate')
            ? dayjs(form.watch('saleDate')).isAfter(dayjs(form.watch('damageDate')))
              ? form.watch('saleDate')
              : form.watch('damageDate')
            : form.watch('saleDate') || form.watch('damageDate');
        if (latestValidDate && selectedDate.isBefore(dayjs(latestValidDate))) {
          return t(TextCodes.Warranty.Block.RepairDateError);
        }
        break;
    }
    return true;
  };

  const filteredProductFields = useMemo(() => {
    return productFields().map((column) =>
      column
        .map((field) => {
          if (Array.isArray(field)) {
            return field.filter((subField) => {
              if (!isBeremaUser && subField.name === 'destinationCode') {
                return false;
              }
              return true;
            });
          }
          if (!isBeremaUser && field.name === 'destinationCode') {
            return null;
          }
          return field;
        })
        .filter((field) => field !== null)
    );
  }, [isBeremaUser]);

  const minDateHandler = (name: keyof Warranty) => {
    if (name === 'damageDate') {
      return form.watch('saleDate');
    }

    if (name === 'repairDate') {
      const saleDate = form.watch('saleDate');
      const damageDate = form.watch('damageDate');

      if (!saleDate && !damageDate) {
        return undefined;
      }
      if (!saleDate) {
        return damageDate;
      }
      if (!damageDate) {
        return saleDate;
      }
      return dayjs(saleDate).isAfter(dayjs(damageDate)) ? saleDate : damageDate;
    }
  };

  return (
    <Grid container spacing={{ xs: 0, sm: 2 }}>
      {filteredProductFields?.map((columnFields, columnIndex) => (
        <Grid item xs={12} sm={6} key={columnIndex}>
          {columnFields.map((field, rowIndex) => {
            if (Array.isArray(field)) {
              return (
                <Grid container key={rowIndex} spacing={{ xs: 0, lg: 2 }}>
                  {field.map((subField) => (
                    <Grid item xs={12} lg={6} key={subField.name}>
                      {subField.name === 'productType' ? (
                        <WarrantyProductSelect />
                      ) : subField.name === 'serialNo' ? (
                        <WarrantySerialNumber
                          label={subField.label}
                          shouldValidateSerial={
                            selectedBrand === 'Honda' || selectedBrand === 'Cramer'
                          }
                          sx={{
                            width: '100%',
                            mt: 5,
                          }}
                          errorText={t(TextCodes.Warranty.Block.SerialNumberIncorrectError)}
                        />
                      ) : subField.name === 'brand' ? (
                        <WarrantyBrandSelect sx={{ mt: 5 }} />
                      ) : subField.type === 'file' ? (
                        <WarrantyFileUpload />
                      ) : subField.type === 'date' ? (
                        <WarrantyDateField
                          name={subField.name}
                          key={subField.name}
                          label={subField.label}
                          required={subField.required}
                          minDate={minDateHandler(subField.name)}
                          validate={validateDate}
                        />
                      ) : (
                        <CustomTextField
                          fullWidth
                          label={subField.label}
                          variant='outlined'
                          type={subField.type}
                          key={subField.name}
                          sx={{ mt: 5 }}
                          error={!!form.formState.errors[subField.name]}
                          {...form.register(subField.name, { required: subField.required })}
                          InputProps={{
                            readOnly: isReadOnly,
                          }}
                        />
                      )}
                    </Grid>
                  ))}
                </Grid>
              );
            } else {
              return field?.type === 'file' && !isSerialNoValid ? (
                <WarrantyFileUpload />
              ) : field?.type === 'date' ? (
                <WarrantyDateField
                  key={field.name}
                  label={field.label}
                  name={field.name}
                  required={field.required}
                  minDate={minDateHandler(field.name)}
                  validate={validateDate}
                />
              ) : (
                field?.name && (
                  <CustomTextField
                    fullWidth
                    key={field?.name}
                    label={field?.label}
                    variant='outlined'
                    type={field?.type}
                    sx={{ mt: 5 }}
                    error={!!form.formState.errors[field?.name]}
                    {...form.register(field?.name, { required: field?.required })}
                    InputProps={{
                      readOnly: isReadOnly,
                    }}
                  />
                )
              );
            }
          })}
        </Grid>
      ))}
    </Grid>
  );
};
